import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { IExamCardProps } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import styles from "../../Styles/examCard.module.css";
import Config from "../../Utils/Config";

/**
 * Reusable ExamCard component for displaying exam information
 * Features:
 * - Exam details display
 * - Enrollment functionality
 * - Status indicators
 * - Responsive design
 */
export default function ExamCard({
  exam,
  onEnrollInExam,
  className,
}: IExamCardProps) {
  const formattedDate = new Date(exam.examDate).toLocaleDateString("ro-RO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const niceDate =
    formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  // Check if enrollment is allowed (at least 5 days before exam)
  const isEnrollmentAllowed = () => {
    const examDate = new Date(exam.examDate);
    const now = new Date();
    const diffInDays = Math.ceil((examDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffInDays >= Config.examEnrollmentMinDays;
  };

  /**
   * Handle exam enrollment
   */
  const handleEnrollClick = () => {
    if (onEnrollInExam && isEnrollmentAllowed()) {
      onEnrollInExam(exam._id);
    }
  };

  return (
    <Card className={`${styles.examCard} ${className || ""}`}>
      <CardContent className={styles.examCardContent}>
        {/* Exam information details */}
        <Box className={styles.examInfo}>
          <Typography variant="h5" className={styles.examDetail}>
            <strong>{Vocabulary.date}:</strong> {niceDate}
          </Typography>
          <Typography variant="body2" className={styles.examCaption}>
            {Vocabulary.upcomingExamTimeNotice}
          </Typography>
        </Box>

        {/* Exam actions - enrollment or enrolled state */}
        <Box className={styles.examActions}>
          {exam.isClientEnrolled ? (
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label={Vocabulary.enrolled}
                color="success"
                variant="filled"
                sx={{ marginBottom: 1 }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  color: '#4caf50',
                  fontWeight: 500,
                  fontStyle: 'italic'
                }}
              >
                {Vocabulary.alreadyEnrolledMessage}
              </Typography>
            </Box>
          ) : !isEnrollmentAllowed() ? (
            <Button
              variant="outlined"
              disabled={true}
              className={styles.enrollButton}
              sx={{ opacity: 0.6 }}
            >
              {`${Vocabulary.enrollInExam} (${Config.examEnrollmentMinDays} ${Vocabulary.daysMinimum})`}
            </Button>
          ) : exam.availableSpots === 0 ? (
            <Button
              variant="contained"
              disabled={true}
              className={styles.enrollButton}
            >
              {Vocabulary.full}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleEnrollClick}
              className={styles.enrollButton}
            >
              {Vocabulary.enrollInExam}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
